import React, { useState, useEffect } from "react";
import { Button } from "../../../ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import Loader from "../../../Loader/Loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { postORputClass } from "../../../../provider/adm/Clases/postORputIndividualClass";
import { toast } from "sonner";
import ScrollListProfesores from "../../Grupos/ScrollListProfesores";

const EditarClasesEnMasa = ({ selectedClasses, onSuccess }) => {
  const [teacherID, setTeacherID] = useState("");
  const [teacherNameprev, setteacherNameprev] = useState("");
  const [classType, setClassType] = useState("");
  const [plannedDuration, setPlannedDuration] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDurationDisabled, setIsDurationDisabled] = useState(false);

  // Función para obtener el valor más común de un array
  const getMostCommonValue = (values) => {
    const frequency = {};
    values.forEach((value) => {
      if (value) {
        frequency[value] = (frequency[value] || 0) + 1;
      }
    });

    let mostCommon = "";
    let maxCount = 0;
    for (const [value, count] of Object.entries(frequency)) {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = value;
      }
    }
    return mostCommon;
  };

  // Verificar si hay clases con estado diferente a "Held"
  const checkDurationEditability = () => {
    const hasNonHeldClasses = selectedClasses.some(
      (cls) => cls.classStatus !== "To be held"
    );
    setIsDurationDisabled(hasNonHeldClasses);
  };

  // Establecer valores por defecto basados en las clases seleccionadas
  useEffect(() => {
    if (selectedClasses && selectedClasses.length > 0) {
      // Verificar si se puede editar la duración
      checkDurationEditability();

      // Obtener el profesor más común
      const teacherIDs = selectedClasses
        .map((cls) => cls.teacherId)
        .filter(Boolean);
      const teacherNames = selectedClasses
        .map((cls) => cls.teacherName)
        .filter(Boolean);
      const mostCommonTeacherID = getMostCommonValue(teacherIDs);
      const mostCommonTeacherName = getMostCommonValue(teacherNames);

      // Obtener el tipo de clase más común
      const classTypes = selectedClasses
        .map((cls) => cls.classType)
        .filter(Boolean);
      const mostCommonClassType = getMostCommonValue(classTypes);

      // Obtener la duración más común
      const durations = selectedClasses
        .map((cls) => cls.plannedDuration?.toString())
        .filter(Boolean);
      const mostCommonDuration = getMostCommonValue(durations);

      // Establecer los valores por defecto
      setTeacherID(mostCommonTeacherID);
      setteacherNameprev(mostCommonTeacherName);
      setClassType(mostCommonClassType);
      setPlannedDuration(mostCommonDuration);
    }
  }, [selectedClasses]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      // Procesar cada clase seleccionada
      for (const classData of selectedClasses) {
        // Crear el objeto con los datos actualizados
        const updatedClassData = {
          adminClassDTO: {
            teacherID: teacherID,
            classType: classType,
            dateTime: classData.dateTime,
            duration: classData.duration,
            participantID: classData.participantID,
            classScope: classData.classScope,
            comment: classData.comment || "",
            topic: classData.topic || "",
            classStatus: classData.classStatus,
            // Solo actualizar plannedDuration si no está deshabilitado
            plannedDuration: isDurationDisabled
              ? classData.plannedDuration
              : parseFloat(plannedDuration),
          },
        };

        // Si la clase está cancelada, incluir datos de cancelación
        if (
          classData.classStatus === "Cancelled" &&
          classData.classCancellation
        ) {
          updatedClassData.classCancellationDTO = {
            durationCancelled: classData.classCancellation.durationCancelled,
            cancellationReason: classData.classCancellation.cancellationReason,
            cancellationTiming: classData.classCancellation.cancellationTiming,
            canceledBy: classData.classCancellation.canceledBy,
            classID: classData.id,
          };
        }
        var response = await postORputClass(
          updatedClassData,
          classData.id,
          false,
          0
        );
      }

      toast.success(
        `${selectedClasses.length} clases actualizadas exitosamente`
      );
      onSuccess(); // Callback para refrescar datos y cerrar dialog
    } catch (error) {
      console.error("Error updating classes:", error);
      toast.error(
        "Error al actualizar las clases, puede ser que el profesor no sea el correcto"
      );
    } finally {
      setLoading(false);
    }
  };

  const tooltipMessage =
    'Alguna de las clases seleccionadas es diferente a "Por llenar" por tanto no se puede modificar la duración planeada de las clases seleccionadas, por favor vuelva a seleccionar las clases y asegúrese de no seleccionar una clase ya registrada para modificar este campo.';

  return (
    <TooltipProvider>
      <div className="overflow-y-auto max-h-[70vh] p-6">
        {loading && <Loader />}

        <DialogHeader>
          <DialogTitle>Editar clases seleccionadas</DialogTitle>
          <DialogDescription>
            Modifica los campos para {selectedClasses.length} clase(s).
            Los valores mostrados son los más comunes entre las clases seleccionadas.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profesor asignado *
            </label>
            <ScrollListProfesores
              setTeacherID={setTeacherID}
              setprofesorSelectedToFilter={setteacherNameprev}
              profesorSelectedToFilter={teacherNameprev}
              setLoading={setLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Valor actual: {teacherNameprev || "No asignado"}
            </p>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 font-semibold">Tipo de clase *:</label>
            <Select
              name="classType"
              onValueChange={setClassType}
              value={classType}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecciona tipo de clase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Virtual">Virtual</SelectItem>
                <SelectItem value="In-person">Presencial</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Valor actual: {classType}</p>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 font-semibold">
              Duración planificada *:
            </label>
            {isDurationDisabled ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-full">
                    <Select
                      name="plannedDuration"
                      disabled={true}
                      value={plannedDuration}
                    >
                      <SelectTrigger className="w-full opacity-50 cursor-not-allowed">
                        <SelectValue placeholder="Campo deshabilitado" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="text-sm">{tooltipMessage}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Select
                name="plannedDuration"
                onValueChange={setPlannedDuration}
                value={plannedDuration}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona duración" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hora</SelectItem>
                  <SelectItem value="1.5">1:30 horas</SelectItem>
                  <SelectItem value="2">2 horas</SelectItem>
                  <SelectItem value="2.5">2:30 horas</SelectItem>
                  <SelectItem value="3">3 horas</SelectItem>
                  <SelectItem value="3.5">3:30 horas</SelectItem>
                  <SelectItem value="4">4 horas</SelectItem>
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {isDurationDisabled ? (
                <span className="text-orange-600 font-medium">
                  Campo deshabilitado - Hay clases ya registradas
                </span>
              ) : (
                `Valor actual: ${plannedDuration} hora(s)`
              )}
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">
              Clases seleccionadas:
            </h4>
            <div className="max-h-32 overflow-y-auto">
              {selectedClasses.map((cls) => (
                <div key={cls.id} className="text-sm text-blue-700 mb-1">
                  ID: {cls.id} -{" "}
                  {new Date(cls.dateTime).toLocaleDateString()} - Profesor:{" "}
                  {cls.teacherName || "No asignado"} - Tipo: {cls.classType} -
                  Duración: {cls.plannedDuration}h -{" "}
                  <span
                    className={`font-medium ${
                      cls.classStatus !== "Held"
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    Estado: {cls.classStatus}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {isDurationDisabled && (
            <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
              <p className="text-sm text-orange-800">
                <strong>⚠️ Advertencia:</strong> {tooltipMessage}
              </p>
            </div>
          )}

          <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
            <p className="text-sm text-yellow-800">
              <strong>Nota:</strong> Los valores mostrados son los más comunes entre
              las clases seleccionadas. Todos los campos son obligatorios y se
              aplicarán a todas las clases seleccionadas.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={handleSubmit}
            className="w-full"
            disabled={
              loading ||
              !teacherID ||
              !classType ||
              (!isDurationDisabled && !plannedDuration)
            }
          >
            Actualizar {selectedClasses.length} clase(s)
          </Button>
        </DialogFooter>
      </div>
    </TooltipProvider>
  );
};

export default EditarClasesEnMasa;
